/**
 * Editorial calendar (C13, P5-04): the month's posts by day (Africa/Douala), and the composer.
 * A post is saved as a draft, its photo or video attached, then scheduled: beat publishes it at
 * its time. In test mode nothing leaves for Meta; every post shows it.
 */

import {
  type ScheduledPost,
  socialPostsCreate,
  socialPostsMediaCreate,
  socialPostsScheduleCreate,
  useSocialPostsDestroy,
  useSocialPostsList,
  useSocialPostsPublishNowCreate,
  useSocialPostsScheduleCreate,
  useSocialPostsUnscheduleCreate,
} from "@agencyos/api-client";
import { Badge, Button, GlassCard, Skeleton, SoftToggle, TextAreaField, TextField } from "@agencyos/ui-web";
import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors } from "../../lib/settings";
import { todayInDouala } from "../billing/shared";
import { dayInDouala, dayText, monthBounds, monthText, type Network, NETWORKS, postTone, shiftMonth, timeText, useRefreshSocial, useSocialRoles } from "./shared";

interface Draft {
  text: string;
  networks: Network[];
  publishAt: string;
  file: File | null;
}

const EMPTY: Draft = { text: "", networks: ["facebook", "instagram"], publishAt: "", file: null };

function Composer() {
  const { t } = useTranslation();
  const refresh = useRefreshSocial();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [done, setDone] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  const save = useMutation({
    mutationFn: async ({ schedule }: { schedule: boolean }) => {
      const created = await socialPostsCreate({
        text: draft.text.trim(),
        networks: draft.networks,
        publish_at: draft.publishAt || null,
        draft: true,
      });
      const id = created.data.id;
      if (draft.file) await socialPostsMediaCreate(id, { file: draft.file });
      if (schedule) await socialPostsScheduleCreate(id);
      return schedule;
    },
    onSuccess: (scheduled) => {
      setDraft(EMPTY);
      if (fileInput.current) fileInput.current.value = "";
      setDone(scheduled ? t("social.composer.scheduled") : t("social.composer.saved"));
      refresh();
    },
    onError: () => refresh(), // the draft may exist already: it shows in the calendar
  });
  const errors = fieldErrors(save.error);
  const toggle = (network: Network, on: boolean) =>
    setDraft({ ...draft, networks: on ? [...draft.networks, network] : draft.networks.filter((n) => n !== network) });
  const submit = (schedule: boolean) => (event?: FormEvent) => {
    event?.preventDefault();
    setDone("");
    save.mutate({ schedule });
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="social-composer-title">
      <h2 id="social-composer-title">{t("social.composer.title")}</h2>
      <form className="stack" onSubmit={submit(true)} noValidate aria-label={t("social.composer.title")}>
        <TextAreaField label={t("social.composer.text")} value={draft.text} rows={4} maxLength={2200} error={errors.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
        <fieldset className="plain-fieldset row">
          <legend>{t("social.composer.networks")}</legend>
          {NETWORKS.map((network) => (
            <SoftToggle key={network} label={t(`social.networks.${network}`)} checked={draft.networks.includes(network)} onChange={(on) => toggle(network, on)} />
          ))}
        </fieldset>
        {errors.networks ? <p role="alert" className="ko">{errors.networks}</p> : null}
        <div className="form-grid">
          <TextField type="datetime-local" label={t("social.composer.publishAt")} hint={t("social.composer.publishAtHint")} value={draft.publishAt} error={errors.publish_at} onChange={(e) => setDraft({ ...draft, publishAt: e.target.value })} />
          <div className="ao-field">
            <label className="ao-field__label" htmlFor="social-composer-file">
              {t("social.composer.media")}
            </label>
            <input id="social-composer-file" ref={fileInput} className="ao-field__input" type="file" accept="image/jpeg,image/png,image/webp,video/mp4" onChange={(e) => setDraft({ ...draft, file: e.target.files?.[0] ?? null })} />
            <p className="ao-field__hint">{t("social.composer.mediaHint")}</p>
          </div>
        </div>
        {errors.media ? <p role="alert" className="ko">{errors.media}</p> : null}
        {save.isError && !Object.keys(errors).length ? <p role="alert" className="ko">{t("common.error")}</p> : null}
        <div className="row">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : t("social.composer.schedule")}
          </Button>
          <Button type="button" variant="secondary" disabled={save.isPending} onClick={() => submit(false)()}>
            {t("social.composer.saveDraft")}
          </Button>
          {done ? <span role="status">{done}</span> : null}
        </div>
      </form>
    </GlassCard>
  );
}

function PostActions({ post }: { post: ScheduledPost }) {
  const { t } = useTranslation();
  const refresh = useRefreshSocial();
  const options = { mutation: { onSettled: refresh } };
  const schedule = useSocialPostsScheduleCreate(options);
  const unschedule = useSocialPostsUnscheduleCreate(options);
  const now = useSocialPostsPublishNowCreate(options);
  const remove = useSocialPostsDestroy(options);
  const busy = schedule.isPending || unschedule.isPending || now.isPending || remove.isPending;
  const failed = [schedule.error, now.error].map((e) => Object.values(fieldErrors(e))[0]).find(Boolean);
  if (post.status === "published" || post.status === "publishing") return null;
  return (
    <div className="stack">
      <div className="row">
        {post.status !== "scheduled" ? (
          <Button variant="secondary" disabled={busy} onClick={() => schedule.mutate({ id: post.id })}>
            {t("social.post.schedule")}
          </Button>
        ) : (
          <Button variant="ghost" disabled={busy} onClick={() => unschedule.mutate({ id: post.id })}>
            {t("social.post.unschedule")}
          </Button>
        )}
        <Button variant="ghost" disabled={busy} onClick={() => now.mutate({ id: post.id })}>
          {t("social.post.publishNow")}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => remove.mutate({ id: post.id })}>
          {t("social.post.delete")}
        </Button>
      </div>
      {failed ? <p role="alert" className="ko">{failed}</p> : null}
    </div>
  );
}

function PostRow({ post, canWork }: { post: ScheduledPost; canWork: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <li className={`social-post social-post--${post.status}`}>
      <div className="row between">
        <strong className="social-post__time ao-num">{post.publish_at ? timeText(post.publish_at, i18n.language) : t("social.post.noDate")}</strong>
        <span className="row">
          {post.networks.map((network) => (
            <Badge key={network}>{t(`social.networks.${network}`)}</Badge>
          ))}
          <Badge tone={postTone(post.status)}>{t(`social.post.statuses.${post.status}`)}</Badge>
          {post.test_mode ? <Badge tone="offer">{t("social.mode.testBadge")}</Badge> : null}
        </span>
      </div>
      <p className="social-post__text">{post.text || "—"}</p>
      {post.media.length ? <p className="muted">{t("social.post.media", { count: post.media.length })}</p> : null}
      {post.error ? <p className="ko">{t("social.post.error", { error: post.error })}</p> : null}
      {canWork ? <PostActions post={post} /> : null}
    </li>
  );
}

export function CalendarTab() {
  const { t, i18n } = useTranslation();
  const { canWork } = useSocialRoles();
  const [month, setMonth] = useState(() => todayInDouala().slice(0, 7));
  const list = useSocialPostsList(monthBounds(month));
  const posts = list.data?.status === 200 ? list.data.data.results : undefined;
  const days = new Map<string, ScheduledPost[]>();
  const undated: ScheduledPost[] = [];
  for (const post of posts ?? []) {
    if (!post.publish_at) undated.push(post);
    else days.set(dayInDouala(post.publish_at), [...(days.get(dayInDouala(post.publish_at)) ?? []), post]);
  }
  const ordered = [...days.entries()].sort(([a], [b]) => a.localeCompare(b));

  let content;
  if (list.isPending) content = <Skeleton />;
  else if (!posts) content = <p role="alert">{t("common.error")}</p>;
  else if (!posts.length) content = <p className="muted">{t("social.calendar.empty")}</p>;
  else
    content = (
      <div className="stack">
        {ordered.map(([day, items]) => (
          <section key={day} className="stack" aria-label={dayText(day, i18n.language)}>
            <h3 className="section-title">{dayText(day, i18n.language)}</h3>
            <ul className="plain-list stack">
              {items.map((post) => (
                <PostRow key={post.id} post={post} canWork={canWork} />
              ))}
            </ul>
          </section>
        ))}
        {undated.length ? (
          <section className="stack" aria-label={t("social.calendar.drafts")}>
            <h3 className="section-title">{t("social.calendar.drafts")}</h3>
            <ul className="plain-list stack">
              {undated.map((post) => (
                <PostRow key={post.id} post={post} canWork={canWork} />
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    );

  return (
    <div className={canWork ? "social-calendar" : "stack"}>
      {canWork ? <Composer /> : null}
      <GlassCard as="section" className="stack social-calendar__list" aria-labelledby="social-calendar-title">
        <div className="row between">
          <h2 id="social-calendar-title">{t("social.calendar.title", { month: monthText(month, i18n.language) })}</h2>
          <span className="row">
            <Button variant="ghost" aria-label={t("social.calendar.previous")} onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft size={16} aria-hidden="true" />
            </Button>
            <Button variant="ghost" aria-label={t("social.calendar.next")} onClick={() => setMonth(shiftMonth(month, 1))}>
              <ChevronRight size={16} aria-hidden="true" />
            </Button>
          </span>
        </div>
        <p className="muted">{t("social.calendar.timezone")}</p>
        {content}
      </GlassCard>
    </div>
  );
}
